import React, { useState } from "react";
import {
  Combobox,
  TextInput,
  Loader,
  useCombobox,
  CloseButton,
} from "@mantine/core";
import { IconMapPin } from "@tabler/icons-react";
import { useAddressAutocomplete } from "@/hooks/useAddressAutocomplete";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

export interface AddressAutocompleteInputProps {
  value: string;
  onChange: (address1: string, fullAddress: AddressData | null) => void;
  customAddresses?: AddressData[];
  placeholder?: string;
  label?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  error?: string | null | undefined;
  required?: boolean;
  disabled?: boolean;
}

export const AddressAutocompleteInput: React.FC<
  AddressAutocompleteInputProps
> = ({
  value,
  onChange,
  customAddresses = [],
  placeholder = "Search for an address",
  label = "Address",
  size = "md",
  error,
  required,
  disabled,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const {
    addressPredictions,
    loading,
    fetchAddressPredictions,
    fetchAddressDetails,
    clearAddressPredictions,
  } = useAddressAutocomplete();

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const handleInputChange = (newValue: string) => {
    onChange(newValue, null);

    if (newValue) {
      fetchAddressPredictions(newValue);
      combobox.openDropdown();
    } else {
      clearAddressPredictions();
      combobox.closeDropdown();
    }

    combobox.updateSelectedOptionIndex();
  };

  const handleClear = () => {
    onChange("", null);
    clearAddressPredictions();
    combobox.closeDropdown();
  };

  const handleCustomAddressSelect = (address: AddressData) => {
    onChange(address.address1 || "", address);
    combobox.closeDropdown();
  };

  const handleGooglePredictionSelect = async (
    addressId: string,
    displayName: string
  ) => {
    setIsLoadingDetails(true);

    try {
      const fullAddress = await fetchAddressDetails(addressId);

      if (fullAddress) {
        onChange(fullAddress.address1 || displayName, fullAddress);
      } else {
        onChange(displayName, null);
      }
    } catch (error) {
      console.error("Failed to fetch address details:", error);
      onChange(displayName, null);
    } finally {
      setIsLoadingDetails(false);
      combobox.closeDropdown();
    }
  };

  const hasCustomAddresses = customAddresses.length > 0;
  const hasGooglePredictions = addressPredictions.length > 0;
  const showDropdown = hasCustomAddresses || hasGooglePredictions;
  const hasValue = value && value.length > 0;

  // Determine right section content
  const getRightSection = () => {
    if (loading || isLoadingDetails) {
      return <Loader size={18} />;
    }
    if (hasValue && !disabled) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label="Clear address"
        />
      );
    }
    return null;
  };

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        const customAddress = customAddresses.find(
          (addr) => addr.addressId === val
        );
        if (customAddress) {
          handleCustomAddressSelect(customAddress);
        } else {
          const prediction = addressPredictions.find(
            (pred) => pred.addressId === val
          );
          if (prediction) {
            handleGooglePredictionSelect(
              prediction.addressId,
              prediction.displayName
            );
          }
        }
      }}
      disabled={disabled}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(event) => handleInputChange(event.currentTarget.value)}
          onClick={() => hasValue && combobox.openDropdown()}
          onFocus={() => hasValue && combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          error={error}
          required={required}
          disabled={disabled}
          rightSection={getRightSection()}
          size={size}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {!showDropdown && hasValue && value.length >= 3 && !loading && (
            <Combobox.Empty>No addresses found</Combobox.Empty>
          )}

          {!showDropdown && hasValue && value.length < 3 && !loading && (
            <Combobox.Empty>
              Type at least 3 characters to search
            </Combobox.Empty>
          )}

          {hasCustomAddresses && (
            <Combobox.Group label="Saved Addresses">
              {customAddresses.map((address) => (
                <Combobox.Option
                  value={address.addressId}
                  key={address.addressId}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <IconMapPin size={16} />
                    <div>
                      <div>{address.displayName || address.address1}</div>
                      {address.address1 &&
                        address.displayName !== address.address1 && (
                          <div style={{ fontSize: "12px", opacity: 0.6 }}>
                            {address.address1}
                            {address.city && `, ${address.city}`}
                            {address.region && `, ${address.region}`}
                          </div>
                        )}
                    </div>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {hasGooglePredictions && (
            <Combobox.Group
              label={hasCustomAddresses ? "Search Results" : undefined}
            >
              {addressPredictions.map((prediction) => (
                <Combobox.Option
                  value={prediction.addressId}
                  key={prediction.addressId}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <IconMapPin size={16} />
                    <div>{prediction.displayName}</div>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
